import { Fragment } from "react";
import { useEffect, useState } from "react";
import FileSaver from "file-saver";
import { toast } from "react-toastify";
import ButtonGroup from "../../../components/ButtonGroup";
import InputGroup from "../../../components/InputGroup";
import InputGroupDate from "../../../components/InputGroupDate";
import Pagination from "../../../components/Pagination";
import VendorTableItem from "./VendorTableItem";
import VendorStatusDropdown from "./VendorStatusDropdown";
import Report from "../../../services/Report";
import { Toast } from "../../../config/Toast";

// Kolom tanggal; key = nama field dari API (lihat DATE_FIELDS di rms-api deposit-vendor-funnel.js)
const DIMINTA = { key: "tgl_diminta", label: "Tgl Diminta" };
const JADWAL_SURVEY = { key: "tgl_jadwal_survey", label: "Tgl Jadwal Survey" };
const DILAKUKAN = { key: "tgl_dilakukan", label: "Tgl Dilakukan" };
const TERKIRIM = { key: "tgl_terkirim", label: "Tgl Terkirim" };
const PROYEK_DIBUAT = { key: "tgl_proyek_dibuat", label: "Tgl Proyek Dibuat" };
const MULAI_KERJA = { key: "tgl_mulai_kerja", label: "Tgl Mulai Kerja" };

const SURVEY_IDS = { idLabel: "ID Admin Panel", shellIdLabel: "ID Shell" };
const PROJECT_IDS = { idLabel: "ID Proyek Admin Panel", shellIdLabel: "ID Shell" };

// Tahap mengikuti Laporan Progres Shell. Tiap tahap difilter dengan tanggal kejadiannya sendiri.
// dates = kolom tanggal di tabel (urut sesuai alur); groupDates menimpa untuk sub-grup tertentu.
// showValue = tampilkan kolom "Nilai Penawaran" (field nilai_penawaran dari API).
const STAGES = [
  {
    key: "survey-diminta",
    label: "Survey diminta",
    ...SURVEY_IDS,
    dates: [DIMINTA, JADWAL_SURVEY],
    groupDates: { cancel: [DIMINTA, JADWAL_SURVEY, { key: "tgl_cancel", label: "Tgl Cancel" }] },
    groups: [
      { key: "homecare", label: "Homecare" },
      // manualDeposit: survey B2B dibayar manual, finance mengisi deposit langsung di tabel
      { key: "dashboard", label: "Dashboard biru (Form & Kerja Tambah)", manualDeposit: true },
      { key: "komplain", label: "Komplain" },
      { key: "cancel", label: "Cancel" },
    ],
  },
  {
    key: "survey-dilakukan",
    label: "Survey dilakukan",
    ...SURVEY_IDS,
    dates: [DIMINTA, DILAKUKAN],
    groups: [
      { key: "dilakukan", label: "Yang dilakukan" },
      { key: "tidak-lanjut", label: "Tidak lanjut kirim penawaran" },
    ],
  },
  { key: "penawaran-terkirim", label: "Penawaran terkirim", ...SURVEY_IDS, dates: [DILAKUKAN, TERKIRIM], showValue: true },
  {
    key: "penawaran-disetujui",
    label: "Penawaran disetujui",
    ...SURVEY_IDS,
    dates: [TERKIRIM, { key: "tgl_disetujui", label: "Tgl Disetujui" }],
    showValue: true,
  },
  { key: "proyek-dibuat", label: "Proyek dibuat", ...PROJECT_IDS, dates: [PROYEK_DIBUAT, MULAI_KERJA] },
  {
    key: "proyek-selesai",
    label: "Proyek selesai",
    ...PROJECT_IDS,
    dates: [PROYEK_DIBUAT, MULAI_KERJA, { key: "tgl_selesai", label: "Tgl Selesai" }],
  },
];

const VendorTable = () => {
  const [data, setData] = useState([]);
  const [update, setUpdate] = useState(false);
  const [btnFilterLoading, setBtnFilterLoading] = useState(false);
  const [btnExcelLoading, setBtnExcelLoading] = useState(false);
  const [btnExcelAllLoading, setBtnExcelAllLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totalPage, setTotalPage] = useState(0);
  const [stage, setStage] = useState(STAGES[0].key);
  const [group, setGroup] = useState(STAGES[0].groups[0].key);

  const notifySuccess = (message) => toast.success(message, Toast);
  const notifyFailed = (message) => toast.error(message, Toast);
  const limit = 5;
  const activeStage = STAGES.find((s) => s.key === stage);
  const activeGroup = activeStage.groups && activeStage.groups.find((g) => g.key === group);
  const editableDeposit = Boolean(activeGroup && activeGroup.manualDeposit);
  const dateColumns = (activeStage.groupDates && activeStage.groupDates[group]) || activeStage.dates;

  useEffect(() => {
    if (startDate && endDate) {
      Report.vendor(pageNumber, limit, search, startDate, endDate, stage, group)
        .then((res) => {
          setData(res.data.data);
          setTotalPage(res.data.total_page);
        })
        .catch((err) => {
          console.log(err);
          notifyFailed("Gagal memuat data");
        });

      setUpdate(false);
      setBtnFilterLoading(false);
    }
  }, [update]);

  const filterHandler = () => {
    if (startDate === null || endDate === null) {
      notifyFailed("Date is required");
      return;
    }

    setBtnFilterLoading(true);
    setUpdate(true);
  };

  const excelHandler = () => {
    if (startDate === null || endDate === null) {
      notifyFailed("Date is required");
      return;
    }

    setBtnExcelLoading(true);

    Report.vendorExcel(startDate, endDate, stage, group)
      .then((res) => {
        const url = `${process.env.REACT_APP_API_DOMAIN}/${res.data.path}`;
        FileSaver.saveAs(url, res.data.path);

        notifySuccess("Success download report deposit");
        setBtnExcelLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setBtnExcelLoading(false);
        notifyFailed("Something went wrong!");
      });
  };

  const excelAllHandler = () => {
    if (startDate === null || endDate === null) {
      notifyFailed("Date is required");
      return;
    }

    setBtnExcelAllLoading(true);

    Report.vendorExcelAll(startDate, endDate)
      .then((res) => {
        const url = `${process.env.REACT_APP_API_DOMAIN}/${res.data.path}`;
        FileSaver.saveAs(url, res.data.path);

        notifySuccess("Success download report deposit");
        setBtnExcelAllLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setBtnExcelAllLoading(false);
        notifyFailed("Something went wrong!");
      });
  };

  // Simpan deposit isian finance (Dashboard biru), lalu muat ulang halaman ini
  const saveDepositHandler = (id, nominal) =>
    Report.vendorDepositManual(id, nominal)
      .then(() => {
        notifySuccess("Deposit berhasil disimpan");
        setUpdate(true);
        return true;
      })
      .catch((err) => {
        console.log(err);
        notifyFailed(err.response?.data?.message || "Gagal menyimpan deposit");
        return false;
      });

  const searchHandler = (value) => {
    setSearch(value);
    setPageNumber(1);
    setUpdate(true);
  };

  const changePageHandler = ({ selected }) => {
    setPageNumber(selected + 1);
    setUpdate(true);
  };

  const reload = () => {
    setData([]);
    setTotalPage(0);
    setPageNumber(1);
    if (startDate && endDate) {
      setUpdate(true);
    }
  };

  const changeStatusHandler = (stageKey, groupKey) => {
    setStage(stageKey);
    setGroup(groupKey);
    reload();
  };

  return (
    <Fragment>
      <VendorStatusDropdown stages={STAGES} stage={stage} group={group} onChange={changeStatusHandler}>
        {btnExcelAllLoading ? (
          <button type="button" className="btn btn-success" disabled>
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Excel All
          </button>
        ) : (
          <button type="button" className="btn btn-success" onClick={excelAllHandler}>
            <i className="fa fa-file-excel-o me-1"></i>
            Excel All
          </button>
        )}
      </VendorStatusDropdown>

      <div className="flex flex-row justify-content-center align-items-end w-100 gap-4">
        <div className="flex flex-row justify-content-center align-items-end w-100 gap-4">
          <div className="w-100">
            <InputGroupDate type="startDate" label="Start Date" setStartDate={setStartDate} />
          </div>
          <div className="w-100">
            <InputGroupDate type="endDate" label="End Date" setEndDate={setEndDate} />
          </div>
        </div>
        <div className="flex flex-row justify-content-center align-items-end w-100">
          <div className="w-100">
            <ButtonGroup
              buttonOne="btn-primary"
              titleOne="Filter"
              iconOne="fa fa-filter me-1"
              onClickOne={filterHandler}
              loadingOne={btnFilterLoading}
              buttonTwo="btn-success"
              titleTwo="Excel"
              iconTwo="fa fa-file-excel-o me-1"
              onClickTwo={excelHandler}
              loadingTwo={btnExcelLoading}
            />
          </div>
          <div className="w-100">
            <InputGroup button="btn-dark" icon="fa fa-search" searchHandler={searchHandler} />
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <div className="table-responsive">
            <table className="table table-bordered table-hover text-nowrap text-md-nowrap mb-0 text-center">
              <thead>
                <tr>
                  <th>No</th>
                  <th>{activeStage.idLabel}</th>
                  <th>{activeStage.shellIdLabel}</th>
                  <th>Project Name</th>
                  <th>Client Name</th>
                  {dateColumns.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                  {activeStage.showValue && <th>Nilai Penawaran</th>}
                  <th>Deposit</th>
                  <th>Payment Method</th>
                  <th>Payment Channel</th>
                  <th>Fee Transfer</th>
                </tr>
              </thead>
              <tbody>
                {data.map((d, index) => (
                  <VendorTableItem
                    key={d.id}
                    no={index + (pageNumber == 1 ? 1 : limit * pageNumber - limit + 1)}
                    data={d}
                    dateColumns={dateColumns}
                    showValue={activeStage.showValue}
                    editableDeposit={editableDeposit}
                    onSaveDeposit={saveDepositHandler}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row mt-5">
        <div className="col">
          {totalPage > 0 && <Pagination pageCount={totalPage} changePage={changePageHandler} forcePage={pageNumber - 1} />}
        </div>
      </div>
    </Fragment>
  );
};

export default VendorTable;
